import type { goalsEn } from "./en.ts";

export const goalsFr: typeof goalsEn = {
    GoalEditView: {
        goalTypeHint: {
            Personal: "Pour le développement personnel ou le travail individuel.",
            Team: "Pour aligner les objectifs partagés, les projets ou le travail transversal d'une équipe.",
            Organization: "Pour rallier tout le monde autour d'une mission commune."
        },
        visibilityHint: {
            AllOrganization: "Toute personne dans l'organisation peut trouver et consulter cet objectif.",
            OwnerAndContributorsOnly: "Toujours visible pour les dirigeants et pour chaque gestionnaire au-dessus du responsable et des contributeurs."
        },
        trackingMethodHint: {
            Simple: "Suivre un seul pourcentage, de 0 % à 100 %.",
            Measurements: "Suivre une ou plusieurs mesures — nombres, devise ou oui/non.",
            SubGoals: "Cumuler la progression des sous-objectifs.",
            SubGoalsAutomatic: "Cumuler la progression des sous-objectifs."
        },
        newGoal: "Nouvel objectif",
        editGoal: "Modifier l'objectif",
        loading: "Chargement de l'objectif",
        loadingMore: "Chargement de plus de résultats",
        searching: "Recherche en cours",
        cancel: "Annuler",
        save: "Enregistrer",
        saveDraft: "Enregistrer comme brouillon",
        publish: "Publier l’objectif",
        assistant: {
            guideSuggestion: "Guide-moi dans la création de cet objectif.",
            ideasSuggestion: "Propose-moi des idées d'objectifs.",
            refineSuggestion: "Aide-moi à peaufiner cet objectif."
        },
        autoSave: {
            saving: "Enregistrement en cours...",
            saved: "Toutes les modifications sont enregistrées",
            error: "Modifications non enregistrées"
        },
        assistantUpdated: "Workleap AI a mis à jour cet objectif",
        discard: {
            heading: "Abandonner vos modifications?",
            body: "Les modifications ne sont pas enregistrées automatiquement. Si vous quittez, elles seront perdues.",
            keepEditing: "Continuer l’édition",
            discard: "Abandonner les modifications"
        },
        trackingMethodChange: {
            heading: "Changer la façon de mesurer la progression?",
            body: "Les mesures de cet objectif seront supprimées dès que votre modification sera enregistrée. Cette action est irréversible.",
            keepMeasurements: "Conserver les mesures",
            confirm: "Supprimer les mesures"
        },
        ownerChange: {
            heading: "Transférer cet objectif ?",
            body: "Vous pouvez modifier cet objectif parce que vous en êtes le propriétaire. Dès qu’une autre personne en sera propriétaire, vous ne pourrez plus y apporter de modifications.",
            keepEditing: "Continuer les modifications",
            confirm: "Changer le propriétaire"
        },
        goalType: {
            Personal: "Personnel",
            Team: "Équipe",
            Organization: "Organisation"
        },
        parentGoalType: {
            Personal: "personnel",
            Team: "d'équipe",
            Organization: "d'organisation"
        },
        visibility: {
            AllOrganization: "Toute l'organisation",
            OwnerAndContributorsOnly: "Responsable et contributeurs seulement"
        },
        trackingMethod: {
            Simple: "Progression simple",
            Measurements: "Mesures",
            SubGoals: "Sous-objectifs",
            SubGoalsAutomatic: "Sous-objectifs"
        },
        validation: {
            emptyField: "Ce champ est vide",
            endBeforeStart: "La date de fin doit être égale ou postérieure à la date de début"
        },
        toast: {
            published: "Objectif publié",
            publishError: "Erreur lors de la publication de l’objectif",
            publishInvalid: "Impossible de publier cet objectif : certains de ses détails sont incomplets ou invalides.",
            saved: "Objectif mis à jour",
            error: "Erreur lors de la mise à jour de l'objectif"
        },
        define: {
            heading: "Définir votre objectif",
            title: "Nom de l'objectif",
            titlePlaceholder: "Saisissez le nom de l'objectif",
            descriptionLabel: "Description",
            descriptionPlaceholder: "Expliquez pourquoi c'est important ou comment vous y arriverez",
            startDate: "Date de début",
            endDate: "Date de fin",
            parent: "Aligner comme sous-objectif de",
            parentPlaceholder: "Recherchez ou sélectionnez un objectif à rattacher",
            noParentResults: "Aucun résultat.",
            parentRestriction: "Cet objectif est de type {{goalType}} : seuls les objectifs {{allowedParentTypes}} peuvent lui servir de parent."
        },
        involved: {
            heading: "Personnes impliquées",
            owner: "Responsable",
            ownerPlaceholder: "Recherchez ou sélectionnez un responsable",
            noOwnerResults: "Vous ne pouvez ajouter que des personnes de votre équipe immédiate (votre gestionnaire et vos pairs directs).",
            goalType: "Qui contribuera à cet objectif?",
            contributors: "Quelles équipes ou personnes contribueront à cet objectif?",
            contributorsPlaceholder: "Rechercher des équipes ou des personnes",
            noContributorResults: "Vous ne pouvez ajouter que des personnes de votre équipe immédiate (votre gestionnaire, vos pairs directs et les équipes dont vous faites partie).",
            contributorsHierarchy: "Quelles personnes ou quels groupes contribueront à cet objectif?",
            contributorsPlaceholderHierarchy: "Rechercher des personnes, des subordonnés directs ou des lignes hiérarchiques",
            noContributorResultsHierarchy: "Vous ne pouvez ajouter que des personnes de votre équipe immédiate (votre gestionnaire et vos pairs directs) et les subordonnés des gestionnaires que vous pouvez voir.",
            members: "Personnes",
            teams: "Équipes",
            directReports: "Subordonnés directs",
            reportingLines: "Lignes hiérarchiques",
            directReportsOf: "Subordonnés directs de {{name}}",
            reportingLineOf: "Ligne hiérarchique de {{name}}",
            refineForMoreGroups: "Seuls les gestionnaires les plus proches de votre recherche sont affichés. Recherchez par nom pour en trouver d’autres.",
            selectedContributors: "Contributeurs sélectionnés",
            contributorLimit: "Vous pouvez sélectionner jusqu’à {{max}} contributeurs.",
            unknownContributor: {
                Member: "Membre supprimé",
                Team: "Équipe inconnue",
                Organization: "Toute l’organisation",
                DirectReports: "Membre supprimé",
                ReportingLine: "Membre supprimé"
            },
            typeRestrictedTitle: "Limité par l'objectif parent",
            typeRestrictedDescription: "Seuls les types {{allowedGoalTypes}} peuvent être sélectionnés tant que cet objectif est lié à un parent {{parentGoalType}}.",
            typeRestrictedHint: "Vous voulez plus de contributeurs? Retirez ou changez le parent sélectionné ci-dessus.",
            typeConflictTitle: "Votre type actuel n'est pas permis sous ce parent",
            typeConflictDescription: "Le parent est un objectif {{parentGoalType}} — choisissez {{allowedGoalTypes}} pour continuer.",
            typeConflictHint: "Vous voulez garder votre type actuel? Retirez ou changez le parent sélectionné ci-dessus.",
            visibility: "Visibilité de l'objectif",
            contributorsRequired: "Un objectif d'équipe requiert au moins un contributeur.",
            contributorsHint_one: "{{count}} contributeur sur cet objectif.",
            contributorsHint_other: "{{count}} contributeurs sur cet objectif."
        },
        measure: {
            heading: "Mesure de la progression",
            methodLabel: "Comment mesurerez-vous cet objectif?",
            autoUpdate: "Mise à jour automatique depuis les sous-objectifs",
            autoUpdateHint: "La progression est calculée à partir des sous-objectifs ci-dessous.",
            columnMeasurement: "Mesure",
            columnSubgoal: "Sous-objectif",
            columnWeight: "Pondération",
            titleLabel: "Titre",
            unitLabel: "Unité",
            startLabel: "Départ",
            targetLabel: "Cible",
            weightLabel: "Pondération",
            weightAuto: "auto %",
            linkLabel: "Lien connexe",
            options: "Options de la mesure",
            optionsFor: "Options pour {{title}}",
            addLink: "Ajouter un lien",
            addPlaceholder: "Ajouter une mesure",
            add: "Ajouter",
            remove: "Retirer la mesure",
            noSubgoals: "Cet objectif n'a pas encore de sous-objectifs à pondérer.",
            weightOver: "La somme des pondérations ne peut dépasser 100 % (actuellement {{total}})",
            weightUnder: "La somme des pondérations doit atteindre 100 % (actuellement {{total}})",
            unit: {
                percentage: "Pourcentage",
                numeric: "Numérique",
                currency: "Devise",
                boolean: "Oui / Non"
            }
        }
    },
    GoalsViewToggle: {
        ariaLabel: "Mode d'affichage",
        tree: "Vue arborescente",
        list: "Vue liste"
    },
    GoalsToolbar: {
        searchPlaceholder: "Rechercher des objectifs",
        searchAriaLabel: "Rechercher des objectifs",
        status: {
            ariaLabel: "Filtrer par statut",
            all: "Tous les statuts",
            onTrack: "Bien avancé",
            atRisk: "À surveiller",
            offTrack: "En difficulté",
            noStatus: "Aucun statut"
        },
        owner: {
            ariaLabel: "Filtrer par responsables",
            label: "Responsables"
        },
        contributor: {
            label: "Contributeurs",
            ariaLabel: "Filtrer par contributeurs"
        },
        goalType: {
            ariaLabel: "Type d'objectif",
            all: "Tous les types",
            organization: "Organisation",
            team: "Équipe",
            individual: "Individuel"
        },
        publicationState: {
            label: "État",
            ariaLabel: "Filtrer par état",
            trigger: "État : {{states}}",
            separator: ", ",
            active: "Actif",
            draft: "Brouillon",
            archived: "Archivé",
            reset: "Rétablir par défaut"
        },
        updatedSince: {
            trigger: "Mis à jour depuis",
            triggerSince: "Depuis le {{date}}",
            dialogTitle: "Filtrer par date de dernière mise à jour",
            fieldLabel: "Mis à jour le ou après le",
            pastWeek: "Semaine passée",
            pastTwoWeeks: "2 dernières semaines",
            pastMonth: "Mois passé",
            clearDate: "Effacer la date"
        },
        notAligned: {
            label: "Non aligné"
        },
        addFilters: {
            label: "Ajouter des filtres"
        },
        clearFilter: "Effacer le filtre",
        clearStatus: "Effacer le filtre de statut",
        clearGoalType: "Effacer le filtre de type d'objectif",
        resetFilters: "Effacer les filtres"
    },
    GoalsTable: {
        untitledGoal: "Objectif sans titre",
        columns: {
            goal: "Objectif",
            due: "Échéance",
            status: "Statut",
            progress: "Progression",
            updated: "Mis à jour",
            actions: "Actions"
        },
        tree: {
            loadingSubgoals: "Chargement des sous-objectifs…",
            subgoalsError: "Impossible de charger les sous-objectifs.",
            retry: "Réessayer"
        }
    },
    GoalRowActions: {
        ariaLabel: "Actions pour {{title}}",
        edit: "Modifier l’objectif",
        updateProgress: "Mettre à jour le progrès",
        duplicate: "Dupliquer l’objectif",
        archive: "Archiver l’objectif",
        unarchive: "Désarchiver l’objectif",
        delete: "Supprimer l’objectif",
        cancel: "Annuler",
        untitled: "Objectif sans titre",
        archiveDialog: {
            title: "Archiver l’objectif",
            description: "{{title}} sortira de vos objectifs actifs. Vous pourrez le désarchiver à tout moment."
        },
        unarchiveDialog: {
            title: "Désarchiver l’objectif",
            description: "{{title}} reviendra dans vos objectifs actifs."
        },
        deleteDialog: {
            title: "Supprimer l’objectif?",
            description: "{{title}} sera supprimé. Cette action est irréversible."
        },
        toast: {
            duplicated: "Objectif dupliqué",
            duplicateError: "Erreur lors de la duplication de l’objectif",
            archived: "Objectif archivé",
            archiveError: "Erreur lors de l’archivage de l’objectif",
            unarchived: "Objectif désarchivé",
            unarchiveError: "Erreur lors du désarchivage de l’objectif",
            deleted: "Objectif supprimé",
            deleteError: "Erreur lors de la suppression de l’objectif"
        }
    },
    GoalRow: {
        deletedMember: "Membre supprimé",
        draft: "Brouillon",
        archived: "Archivé",
        untitled: "Objectif sans titre",
        status: {
            onTrack: "Bien avancé",
            atRisk: "À surveiller",
            offTrack: "En difficulté",
            noStatus: "Aucun statut"
        },
        type: {
            organization: "Organisation",
            team: "Équipe",
            personal: "Individuel"
        },
        noDate: "Aucune date",
        overdue: "En retard",
        progressAriaLabel: "Progression de l'objectif",
        progressPercent: "{{value}} %",
        updated: {
            never: "Jamais",
            today: "Aujourd'hui",
            days: "il y a {{count}} j",
            weeks: "il y a {{count}} sem"
        },
        tree: {
            expand: "Afficher les objectifs alignés",
            collapse: "Masquer les objectifs alignés"
        }
    }
};
